/**
 * 蜜糖黄 · 阳光黄页 信息详情页
 *
 * 版式：左侧主内容(文字描述 + 竖排方图集 + 安全提示 + 同类推荐)，右侧登录可见联系方式卡。
 * 规范要点：图片 flex flex-col 竖排展示全部、无顶部大图轮播、无 grid 图集；公司名受 hide_company_name 控制。
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getListingDetail, getListings, type ListingItem } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import HoneyNavbar from "@/templates/honey/components/Navbar";
import HoneyFooter from "@/templates/honey/components/Footer";

function formatDateTime(dateStr?: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatTimeAgo(dateStr?: string) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} 个月前`;
  return `${Math.floor(months / 12)} 年前`;
}

function formatViewCount(count?: number) {
  if (!count) return "0";
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`;
  return String(count);
}

const SAFETY_TIPS = [
  "请勿轻信任何要求提前转账、付款、垫资的要求，谨防上当受骗。",
  "本平台仅提供信息展示，交易前请务必自行核实对方身份、资质及物品真实性。",
  "如遇虚假信息或欺诈行为，请及时向平台举报。",
];

export default function HoneyListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { siteConfig } = useSiteConfig();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedList, setRelatedList] = useState<ListingItem[]>([]);
  const [showPhone, setShowPhone] = useState(false);
  const [copied, setCopied] = useState("");
  const [contactLoading] = useState(false);

  const hideCompanyName = siteConfig?.hide_company_name ?? false;

  useEffect(() => {
    if (!id) return;
    let ignore = false;
    setLoading(true);
    setNotFound(false);
    setDetail(null);
    setShowPhone(false);
    setCopied("");

    getListingDetail(String(id))
      .then((data) => {
        if (ignore) return;
        if (!data) {
          setNotFound(true);
          return;
        }
        setDetail(data);
        // 同类推荐
        getListings({ category_id: data.category_id, page: 1, page_size: 8 })
          .then((res) => {
            const arr = Array.isArray(res.list) ? res.list.filter((it: ListingItem) => it.id !== Number(id)).slice(0, 4) : [];
            setRelatedList(arr);
          })
          .catch(() => setRelatedList([]));
      })
      .catch(() => {
        if (!ignore) setNotFound(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [id]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1600);
    } catch {
      setCopied("");
    }
  };

  /* ── 骨架屏 ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9EC]">
        <HoneyNavbar />
        <div className="lg:pl-60">
          <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8 animate-pulse">
            <div className="h-3 w-48 rounded bg-[#F3ECDC]" />
            <div className="mt-4 h-7 w-2/3 rounded bg-[#F3ECDC]" />
            <div className="mt-8 grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-56 rounded-xl bg-[#F3ECDC]" />
                <div className="h-64 rounded-xl bg-[#F3ECDC]" />
              </div>
              <div className="h-80 rounded-xl bg-[#F3ECDC]" />
            </div>
          </section>
        </div>
      </div>
    );
  }

  /* ── 信息不存在 ── */
  if (notFound || !detail) {
    return (
      <div className="min-h-screen bg-[#FFF9EC]">
        <HoneyNavbar />
        <div className="lg:pl-60">
          <section className="max-w-3xl mx-auto px-4 py-24 text-center">
            <i className="ri-error-warning-line text-6xl text-[#E4D9BB]" />
            <h1 className="mt-5 text-2xl font-black text-[#23201A]">信息不存在或已被删除</h1>
            <p className="mt-2 text-sm text-[#8B8577]">您可以返回首页浏览其他信息</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="px-6 py-2.5 rounded-xl bg-[#23201A] text-[#FFF9EC] text-sm font-bold hover:bg-[#3A3529] transition-colors cursor-pointer"
              >
                返回首页
              </button>
              <Link
                to="/category"
                className="px-6 py-2.5 rounded-xl border border-[#EAE1CB] bg-white text-[#6E6658] text-sm font-bold hover:border-[#F0A500] hover:text-[#B87A00] transition-colors"
              >
                浏览分类
              </Link>
            </div>
          </section>
        </div>
      </div>
    );
  }

  const imageList: string[] = Array.isArray(detail.images) ? detail.images : [];
  const paragraphs = typeof detail.content === "string" ? detail.content.split(/\n+/) : [];
  const hasContact =
    (detail.contact_phone || detail.contact_wechat || detail.contact_name || detail.contact_email);
  const contactItems = [
    { icon: "ri-user-smile-line", label: "联系人", value: detail.contact_name },
    { icon: "ri-phone-line", label: "电话", value: detail.contact_phone },
    { icon: "ri-wechat-line", label: "微信", value: detail.contact_wechat },
    { icon: "ri-mail-line", label: "邮箱", value: detail.contact_email },
  ].filter((item) => item.value);

  const metaRows = [
    { icon: "ri-price-tag-3-line", label: "所属分类", value: detail.category_name },
    { icon: "ri-time-line", label: "发布时间", value: formatDateTime(detail.created_at) },
    { icon: "ri-eye-line", label: "浏览量", value: `${formatViewCount(detail.view_count)} 次浏览` },
  ];

  return (
    <div className="min-h-screen bg-[#FFF9EC]">
      <HoneyNavbar />
      <div className="lg:pl-60">
        {/* 面包屑 */}
        <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6">
          <nav className="flex items-center flex-wrap gap-1.5 text-xs text-[#8B8577]">
            <Link to="/" className="hover:text-[#B87A00] transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-[#B87A00] transition-colors">分类</Link>
            {detail.category_slug && (
              <>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/category/${detail.category_slug}`} className="text-[#B87A00] font-semibold hover:underline">
                  {detail.category_name || "分类"}
                </Link>
              </>
            )}
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#6E6658] truncate max-w-[180px]">{detail.title}</span>
          </nav>
        </div>

        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
          <div className="grid lg:grid-cols-3 gap-6 items-start">
            {/* ═════════ 左：主内容 ═════════ */}
            <div className="lg:col-span-2 space-y-6">
              {/* 标题卡 */}
              <div className="bg-white border border-[#EAE1CB] rounded-2xl p-5 sm:p-7">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#B87A00] bg-[#FFF3D6] rounded-full px-2.5 py-1">
                    <i className="ri-folder-line" />
                    {detail.category_name || "分类信息"}
                  </span>
                  {detail.is_pinned && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#23201A] text-[#F0A500] rounded-full px-2.5 py-1">
                      <i className="ri-pushpin-2-fill" />置顶
                    </span>
                  )}
                </div>
                <h1 className="mt-3 text-xl sm:text-2xl font-black text-[#23201A] leading-snug">{detail.title}</h1>
                <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#8B8577]">
                  <span className="inline-flex items-center gap-1"><i className="ri-time-line" />{formatTimeAgo(detail.created_at)}发布</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-eye-line" />{formatViewCount(detail.view_count)} 次浏览</span>
                </p>
              </div>

              {/* 内容详情 */}
              <div className="bg-white border border-[#EAE1CB] rounded-2xl p-5 sm:p-7">
                <h2 className="flex items-center gap-2 text-base font-black text-[#23201A]">
                  <span className="w-1.5 h-5 bg-[#F0A500] rounded-full inline-block" />详细信息
                </h2>
                <div className="mt-4 text-sm text-[#4A453B] leading-7 whitespace-pre-line">
                  {paragraphs.length > 0
                    ? paragraphs.map((p, i) => <p key={i} className={p.trim() ? "mb-3" : "mb-0"}>{p}</p>)
                    : detail.content || "该信息暂无详细描述，欢迎联系发布者了解。"
                  }
                </div>
              </div>

              {/* 竖排图集：全部图片 */}
              {imageList.length > 0 && (
                <div className="bg-white border border-[#EAE1CB] rounded-2xl p-5 sm:p-7">
                  <h2 className="flex items-center gap-2 text-base font-black text-[#23201A]">
                    <span className="w-1.5 h-5 bg-[#F0A500] rounded-full inline-block" />图片展示（{imageList.length}）
                  </h2>
                  <div className="mt-4 flex flex-col gap-4">
                    {imageList.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`${detail.title} 图片 ${idx + 1}`}
                        loading="lazy"
                        className="w-full aspect-square object-cover rounded-xl border border-[#EAE1CB]"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 安全提示 */}
              <div className="bg-[#FFF3D6] border border-[#F0D98C] rounded-2xl p-5">
                <h2 className="flex items-center gap-2 text-sm font-black text-[#B87A00]">
                  <i className="ri-shield-check-line text-base" />温馨提示
                </h2>
                <ul className="mt-3 space-y-2">
                  {SAFETY_TIPS.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#6E5A2A] leading-5">
                      <i className="ri-checkbox-circle-line mt-0.5 shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ═════════ 右：联系卡 ═════════ */}
            <aside className="space-y-5 lg:sticky lg:top-6">
              {/* 联系方式（登录后可见） */}
              <div className="bg-white border border-[#EAE1CB] rounded-2xl overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-[#F0A500] via-[#FFBE30] to-[#F0A500]" />
                <div className="p-5 sm:p-6">
                  <h2 className="flex items-center gap-2 text-base font-black text-[#23201A]">
                    <i className="ri-contacts-book-line text-[#F0A500]" />联系方式
                  </h2>

                  {isLoggedIn ? (
                    contactItems.length > 0 ? (
                      <div className="mt-4 space-y-3.5">
                        {contactItems.map((item) => (
                          <div key={item.label} className="flex items-center justify-between gap-3 text-sm">
                            <span className="inline-flex items-center gap-2 text-[#6E6658] text-xs w-16 shrink-0">
                              <i className={`${item.icon} text-[#B87A00]`} />
                              {item.label}
                            </span>
                            <span className="flex items-center gap-2 min-w-0">
                              <span className="text-[#23201A] font-bold truncate">{item.value}</span>
                              {item.label === "微信" && (
                                <button
                                  onClick={() => copyText(item.value, "wechat")}
                                  className="shrink-0 text-[11px] text-[#B87A00] font-semibold hover:underline cursor-pointer"
                                >
                                  {copied === "wechat" ? "已复制" : "复制"}
                                </button>
                              )}
                              {item.label === "电话" && (
                                <button
                                  onClick={() => setShowPhone(true)}
                                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#F0A500] text-[#23201A] text-[11px] font-bold hover:bg-[#FFB520] transition-colors cursor-pointer"
                                >
                                  <i className="ri-phone-line text-xs" />
                                  {showPhone ? "已显示" : "拨打"}
                                </button>
                              )}
                            </span>
                          </div>
                        ))}
                        {showPhone && detail.contact_phone && (
                          <a
                            href={`tel:${detail.contact_phone}`}
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-black hover:bg-[#FFB520] transition-colors"
                          >
                            <i className="ri-phone-line" />拨打电话：{detail.contact_phone}
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-[#8B8577]">该信息暂未提供联系方式</p>
                    )
                  ) : (
                    <div className="mt-4 bg-[#FFF3D6] border border-dashed border-[#F0D98C] rounded-xl p-5 text-center">
                      <i className="ri-lock-2-line text-2xl text-[#B87A00]" />
                      <p className="mt-2 text-sm text-[#6E5A2A] font-semibold">登录后可见联系方式</p>
                      <p className="mt-1 text-xs text-[#8B8577]">请登录后与发布者取得联系</p>
                      <Link
                        to="/login"
                        className="mt-4 inline-flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl bg-[#23201A] text-[#F0A500] text-sm font-bold hover:bg-[#3A3529] transition-colors"
                      >
                        <i className="ri-login-circle-line" />立即登录
                      </Link>
                    </div>
                  )}
                  {contactLoading && <p className="mt-3 text-xs text-[#8B8577]">加载中…</p>}
                </div>
              </div>

              {/* 发布者 / 公司（受 hide_company_name 控制） */}
              {!hideCompanyName && detail.company_name && (
                <div className="bg-white border border-[#EAE1CB] rounded-2xl p-5">
                  <h2 className="flex items-center gap-2 text-sm font-black text-[#23201A]">
                    <i className="ri-store-2-line text-[#F0A500]" />发布者
                  </h2>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-[#23201A] text-[#F0A500] flex items-center justify-center shrink-0">
                      <i className="ri-building-2-line" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-[#23201A] truncate">{detail.company_name}</p>
                      <p className="text-[11px] text-[#8B8577] mt-0.5">平台认证发布者</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 信息档案 */}
              <div className="bg-white border border-[#EAE1CB] rounded-2xl p-5">
                <h2 className="flex items-center gap-2 text-sm font-black text-[#23201A]">
                  <i className="ri-file-info-line text-[#F0A500]" />信息档案
                </h2>
                <dl className="mt-3 divide-y divide-dashed divide-[#EAE1CB]">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-3 py-2.5 text-[13px]">
                      <dt className="inline-flex items-center gap-1.5 text-[#6E6658]">
                        <i className={`${row.icon} text-[#B87A00]`} />
                        {row.label}
                      </dt>
                      <dd className="text-[#23201A] font-semibold text-right">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </aside>
          </div>
        </section>

        {/* ═════════ 同类推荐 ═════════ */}
        {relatedList.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-12">
            <div className="flex items-end justify-between mb-5">
              <h2 className="text-lg font-black text-[#23201A] flex items-center gap-2">
                <span className="w-1.5 h-5 bg-[#F0A500] rounded-full inline-block" />同类推荐
              </h2>
              <Link to="/category" className="text-[13px] font-bold text-[#B87A00] hover:text-[#23201A] transition-colors">
                更多推荐 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedList.map((item) => {
                const cover = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : "";
                return (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="group bg-white border border-[#EAE1CB] rounded-xl overflow-hidden hover:border-[#F0A500] hover:shadow-lg hover:shadow-[#F0A500]/10 hover:-translate-y-0.5 transition-all"
                  >
                    <div className="aspect-square overflow-hidden bg-[#FFF7E0]">
                      {cover ? (
                        <img src={cover} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-3xl text-[#E9D9AE]" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-[13px] font-bold text-[#23201A] line-clamp-1 group-hover:text-[#B87A00] transition-colors">{item.title}</p>
                      <p className="mt-1 text-[11px] text-[#8B8577]">{formatTimeAgo(item.created_at)}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <HoneyFooter />
      </div>
    </div>
  );
}
